/**
 * CompactMessageRow — one row of the "By List" data table.
 *
 * Columns are fixed-width cells (no flex gaps) so every row lines up with the
 * resizable header {@link CompactMessageList} draws: a leading gutter (select ·
 * star · attachment), then Subject (bold if unread) · Correspondents · Date, an
 * elastic spacer, then a fixed cluster of action columns — reply / reply-all /
 * forward · spam / archive / delete — so every message is actionable without
 * expanding. Any of Subject / Correspondents / Date / Actions can be hidden via
 * the header's settings. The trailing down-arrow expands the row to reveal ONLY
 * the body: the snippet (clamped to the preview-lines preference) and a "Full
 * message" link. Every action reuses the list's existing handlers.
 */

import { useState } from 'react';
import {
  Star,
  Paperclip,
  ChevronDown,
  ChevronRight,
  Reply,
  ReplyAll,
  Forward,
  ShieldAlert,
  Archive,
  Trash2,
  Mail,
  UserCheck,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ByListColumnVisibility } from '../hooks/useEmailLayoutPrefs';
import type { EmailMessageDto } from '@/lib/api';

const PREVIEW_CLAMP: Record<1 | 2 | 3, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

export interface CompactMessageRowProps {
  message: EmailMessageDto;
  /** Sent/Drafts folders show recipients ("To: …") instead of the sender. */
  showRecipients: boolean;
  previewLines: 1 | 2 | 3;
  selectionMode: boolean;
  selected: boolean;
  /** Column widths (px), owned by the list so header + rows stay aligned. */
  leadWidth: number;
  subjectWidth: number;
  correspondentsWidth: number;
  dateWidth: number;
  /** Which columns are shown (from the view's settings). */
  visibility: ByListColumnVisibility;
  /** Screener view: hide the star, and offer Approve / Reject sender instead of
      the normal row actions (matching the standard screener list). */
  screenerView: boolean;
  onSelect: (shiftKey: boolean) => void;
  onOpenFull: () => void;
  onStar: () => void;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onReportSpam: () => void;
  onArchive: () => void;
  onTrash: () => void;
  onApproveSender: () => void;
  onRejectSender: () => void;
  formatDate: (iso: string) => string;
}

export function CompactMessageRow({
  message: m,
  showRecipients,
  previewLines,
  selectionMode,
  selected,
  leadWidth,
  subjectWidth,
  correspondentsWidth,
  dateWidth,
  visibility,
  screenerView,
  onSelect,
  onOpenFull,
  onStar,
  onReply,
  onReplyAll,
  onForward,
  onReportSpam,
  onArchive,
  onTrash,
  onApproveSender,
  onRejectSender,
  formatDate,
}: CompactMessageRowProps) {
  const [expanded, setExpanded] = useState(false);
  const date = m.receivedAt ?? m.sentAt ?? m.createdAt;
  const correspondents = showRecipients
    ? `To: ${m.toAddresses.map((a) => a.name || a.email).join(', ') || '(no recipient)'}`
    : m.fromName || m.fromAddress;
  const composeShown = visibility.reply || visibility.replyAll || visibility.forward;
  const triageShown = visibility.spam || visibility.archive || visibility.delete;
  const anyAction = composeShown || triageShown;

  return (
    <div className={`border-b text-sm ${selected ? 'bg-accent/40' : ''}`}>
      <div className="flex items-center pr-1 hover:bg-accent/40">
        {/* Leading gutter — fixed width so every row's columns line up. */}
        <div
          className="flex shrink-0 items-center gap-1 py-1 pl-2 pr-1"
          style={{ width: leadWidth }}
        >
          {selectionMode && (
            <input
              type="checkbox"
              className="h-3.5 w-3.5 shrink-0"
              aria-label={`Select ${m.subject || '(no subject)'}`}
              checked={selected}
              readOnly
              onMouseDown={(e) => {
                if (e.shiftKey) e.preventDefault();
              }}
              onClick={(e) => onSelect(e.shiftKey)}
            />
          )}
          {visibility.star && !screenerView && (
            <button
              type="button"
              title={m.isStarred ? 'Unstar' : 'Star'}
              aria-label={m.isStarred ? 'Unstar message' : 'Star message'}
              aria-pressed={m.isStarred}
              onClick={onStar}
              className={`shrink-0 rounded p-0.5 transition-colors ${
                m.isStarred ? 'text-yellow-500' : 'text-muted-foreground hover:text-yellow-500'
              }`}
            >
              <Star className={`h-3.5 w-3.5 ${m.isStarred ? 'fill-current' : ''}`} />
            </button>
          )}
          {visibility.attachments && (
            <span className="flex w-3.5 shrink-0 justify-center text-muted-foreground">
              {m.hasAttachments && <Paperclip className="h-3.5 w-3.5" />}
            </span>
          )}
        </div>
        {/* Subject column. */}
        {visibility.subject && (
          <button
            type="button"
            onClick={onOpenFull}
            className="flex shrink-0 items-center overflow-hidden py-1 pr-2 text-left"
            style={{ width: subjectWidth }}
            title={m.subject || '(no subject)'}
          >
            <span className={`block truncate ${m.isRead ? '' : 'font-semibold'}`}>
              {m.subject || '(no subject)'}
            </span>
          </button>
        )}
        {/* Correspondents column. */}
        {visibility.correspondents && (
          <button
            type="button"
            onClick={onOpenFull}
            className="flex shrink-0 items-center overflow-hidden py-1 pr-2 text-left text-xs text-muted-foreground"
            style={{ width: correspondentsWidth }}
            title={correspondents}
          >
            <span className="block truncate">{correspondents}</span>
          </button>
        )}
        {/* Date column. */}
        {visibility.date && (
          <span
            className="flex shrink-0 items-center overflow-hidden py-1 pr-2 text-[11px] tabular-nums text-muted-foreground"
            style={{ width: dateWidth }}
          >
            {date ? formatDate(date) : ''}
          </span>
        )}
        <span className="flex-1" />
        {/* Screener view: the row decision is Approve / Reject sender, in place of
            the normal actions. */}
        {screenerView ? (
          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:text-emerald-600"
              title="Approve sender — let their mail into your inbox"
              aria-label="Approve sender"
              onClick={onApproveSender}
            >
              <UserCheck className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-destructive"
              title="Reject sender — move their mail to Trash"
              aria-label="Reject sender"
              onClick={onRejectSender}
            >
              <Ban className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          anyAction && (
            <div className="flex shrink-0 items-center gap-0.5">
              {visibility.reply && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  title="Reply"
                  aria-label="Reply"
                  onClick={onReply}
                >
                  <Reply className="h-3.5 w-3.5" />
                </Button>
              )}
              {visibility.replyAll && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  title="Reply all"
                  aria-label="Reply all"
                  onClick={onReplyAll}
                >
                  <ReplyAll className="h-3.5 w-3.5" />
                </Button>
              )}
              {visibility.forward && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  title="Forward"
                  aria-label="Forward"
                  onClick={onForward}
                >
                  <Forward className="h-3.5 w-3.5" />
                </Button>
              )}
              {composeShown && triageShown && <span className="mx-0.5 h-4 w-px bg-border" />}
              {visibility.spam && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  title="Report spam"
                  aria-label="Report spam"
                  onClick={onReportSpam}
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                </Button>
              )}
              {visibility.archive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  title="Archive"
                  aria-label="Archive message"
                  onClick={onArchive}
                >
                  <Archive className="h-3.5 w-3.5" />
                </Button>
              )}
              {visibility.delete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-destructive"
                  title="Delete"
                  aria-label="Delete message"
                  onClick={onTrash}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )
        )}
        <button
          type="button"
          aria-label={expanded ? 'Collapse message' : 'Expand message'}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="ml-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </div>
      {/* Pulldown reveals ONLY the body — actions live on the row above. */}
      {expanded && (
        <div className="space-y-2 bg-muted/30 px-3 py-2">
          <p
            className={`whitespace-pre-wrap text-xs text-muted-foreground ${PREVIEW_CLAMP[previewLines]}`}
          >
            {m.snippet || '(no preview)'}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            title="Open the full message"
            onClick={onOpenFull}
          >
            <Mail className="h-3.5 w-3.5" /> Full message
          </Button>
        </div>
      )}
    </div>
  );
}
